import { Placeholder, stories, storiesTree } from 'virtual:tidal-sandbox-list'
import type { TreeItem } from '@/types'
import type { SearchResultItem } from '../components/atoms/searchResults.js'
import { PreviewPlaceholder } from '../components/PreviewPlaceholder.js'

export function getStoriesTree(): Readonly<Record<string, TreeItem>> {
  return storiesTree
}

function matchItemId(id: string, item: TreeItem) {
  return item.type === 'item' && item.uid === id
}

function matchNodeId(id: string, item: TreeItem) {
  return item.type === 'node' && item.uid === id
}

function matchSlug(slug: string, item: TreeItem) {
  return item.slug === slug
}

export function findBySlug(slug: string) {
  for (const item of getStoryIterator()) {
    if (matchSlug(slug, item)) return item
  }
}

export function findNodeById(id: string) {
  for (const item of getStoryIterator()) {
    if (matchNodeId(id, item)) return item
  }
}

export function findItemById(id: string) {
  for (const item of getStoryIterator()) {
    if (matchItemId(id, item)) return item
  }
}

export function findNodeByIdWithPath(id: string) {
  return findItemWithPath(item => matchNodeId(id, item))
}

export function findBySlugWithPath(slug: string) {
  return findItemWithPath(item => matchSlug(slug, item))
}

export function storyExists(slug: string): boolean {
  return !!findBySlug(slug)
}

export function getStoryComponent(slug: string) {
  return stories[slug]?.component
}

export function getTreeTopLevelIds() {
  return Object.values(storiesTree).map(storyRoot => storyRoot.uid)
}

export function getPlaceholderComponent() {
  return Placeholder ?? PreviewPlaceholder
}

type FindItemWithPathFn = (item: TreeItem) => boolean
type FindItemWithPathResult = { item: TreeItem; path: TreeItem[] } | undefined
function findItemWithPath(fn: FindItemWithPathFn): FindItemWithPathResult {
  const items = getStoryIterator()
  items.next() // skip Root

  const path: TreeItem[] = []

  for (const item of items) {
    // pop nodes that parent our parent
    for (let i = path.length - 1; i >= 0; i--) {
      if (path[i].children?.includes(item)) break
      path.pop()
    }

    if (item.type === 'node') {
      path.push(item)
    }

    if (fn(item)) {
      return {
        item,
        path,
      }
    }
  }
}

// ------------------------------------------------
// ------------------------------------------------

export type SearchMatchToken = {
  value: string
  match?: boolean
}

export type BaseSearchMatch = {
  id: string
  title: string
  nodePath: string[]
}

export interface SearchMatch extends BaseSearchMatch {
  packageName: string
  componentName: string
  matchingTags: string[]
  titleTokens: SearchMatchToken[]
  componentNameTokens?: SearchMatchToken[]
  nodePathTokens: SearchMatchToken[][]
}
export interface GroupedSearchMatch {
  [component: string]: SearchMatch[]
}

// ------------------------------------------------
// ------------------------------------------------

function treeItemToSearchResultItem(node: TreeItem, nodePath: string[]): SearchResultItem {
  return {
    id: node.type === 'item' ? node.slug : node.uid,
    label: node.title,
    path: nodePath.join('/'), // FIXME: what was used for? Do we ever used it? --> node.displayTags.slice(0, 2),
    tags: [],
    type: node.type,
  }
}

function searchStory(search: string, node: TreeItem, nodePath: string[], matches: SearchResultItem[]) {
  // const hasMatchingTags = node.searchTags.some(tag => tag.includes(search))
  const hasMatchingTags = node.searchTags.some(tag => tag.indexOf(search) > -1)

  const childMatches: SearchResultItem[] = []

  const childPath = nodePath.concat(node.title)
  node.children?.forEach(child => {
    searchStory(search, child, childPath, childMatches)
  })

  if (hasMatchingTags || childMatches.length > 0) {
    if (node.type === 'item') {
      matches.push(treeItemToSearchResultItem(node, nodePath))
    } else {
      const match = treeItemToSearchResultItem(node, childPath)
      const keepNode = childMatches.some(x => x.type === 'item' && x.path === match.path)

      if (keepNode) matches.push(match)

      matches.push(...childMatches)
    }
  }
}

export function searchStories(search: string): SearchResultItem[] {
  const matches: SearchResultItem[] = []

  for (const node of Object.values(getStoriesTree())) {
    searchStory(search.toLowerCase(), node, [], matches)
  }

  matches.sort((a, b) => a.path.localeCompare(b.path))
  return matches
}

// ------------------------------------------------
// ------------------------------------------------

export function* getStoryIterator(item: TreeItem = getTreeRoot()): Generator<TreeItem, TreeItem | undefined> {
  yield item
  if (item.type === 'node') {
    for (const child of item.children ?? []) {
      yield* getStoryIterator(child)
    }
  }
  return
}

function getTreeRoot(): TreeItem {
  return {
    children: Object.values(getStoriesTree()),
    searchTags: [],
    slug: 'root',
    title: 'Root',
    type: 'node',
    uid: 'root',
  }
}
